/* eslint-disable react/jsx-props-no-spreading */
import {
  Typography,
  Button,
  Modal,
  SliderProps,
  IconButton
} from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState, useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import ClearIcon from '@mui/icons-material/Clear';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import useCompanyStore from '../../../store/zustand/company';
import { secondaryColors } from '../../../theme/themeConfig';
import api from '../../../api';
import AutoCompleteInput from '../../../components/input/AutoCompleteInput';
import DatePickerTextField from './components/DatePickerTextField';
import { parseTemplate } from '../utils/parseTemplate';
import { readFileContent } from '../../../utils/storageHelper';
import {
  ContractHeaderVariablesEnum,
  PaymentTermsVariablesEnum
} from '../../../helpers/editorVariables';

import PdfViewerComponent from '../../../components/shared/container/pdfViewerContainer';
import { formatDate } from '../../../utils/general';

interface SelectTermsAndConditionsProps {
  proposalData: any;
  selectedTerms: any;
  setPdfFilePayload: (values: any) => void;
  setSelectedTerms: (value: any) => void;
  values: any;
}

export const ModalContainer = styled(Box)<SliderProps>(() => ({
  backgroundColor: 'white',
  boxShadow: '24',
  height: '100%',
  padding: '5px 40px',
  position: 'absolute' as 'absolute',
  right: '0',
  top: '0',
  width: 600
}));

const SelectTermsAndConditions: React.FC<SelectTermsAndConditionsProps> =
  function SelectTermsAndConditions({
    proposalData,
    values,
    selectedTerms,
    setSelectedTerms,
    setPdfFilePayload
  }: SelectTermsAndConditionsProps) {
    const navigate = useNavigate();
    const { company } = useCompanyStore((state) => state);
    const [allTermsAndConditions, setAllTermsAndConditions] = useState<any>([]);
    const [getAllTermsAndConditionsAPI, setGetAllTermsAndConditionsAPI] =
      useState(true);
    const [openPreview, setOpenPreview] = useState(false);

    // PDF Generation States
    const [getHeaderAPI, setGetHeaderAPI] = useState(false);
    const [getTermsAPI, setGetTermsAPI] = useState(false);
    const [headerFiles, setHeaderFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [PDFUrl, setPDFUrl] = useState('');
    const [template, setTemplate] = useState('');
    const [termsFiles, setTermsFiles] = useState<any[]>([]);
    const [filePayload, setFilePayload] = useState({});

    const {
      data,
      error,
      isValidating: termsAndConditionsValidating
    } = useSWR(
      getAllTermsAndConditionsAPI ? {} : null,
      api.TermsAndConditionsApi.getAllTermsAndConditions
    );

    // PDF generation API calls
    const {
      data: termsFileData,
      error: termsError,
      mutate: mutateTerms
    } = useSWR(
      getTermsAPI ? { id: selectedTerms?.id } : null,
      api.StorageApi.getTermsAndConditions
    );

    const {
      data: headerData,
      error: headerError,
      mutate: mutateHeader
    } = useSWR(
      getHeaderAPI ? { id: company?.id } : null,
      api.StorageApi.getContractHeader
    );

    useEffect(() => {
      if (data) {
        setAllTermsAndConditions(data);
        if (!selectedTerms && data?.length > 0) {
          setSelectedTerms(data[0]);
          setLoading(true);
        }
      }
    }, [data]);

    useEffect(() => {
      if (error) {
        setGetAllTermsAndConditionsAPI(false);
      }
    }, [error]);

    useEffect(() => {
      return () => {
        mutateTerms(undefined, false);
        mutateHeader(undefined, false);
      };
    }, []);

    const autoCompleteCustom = (
      key: string,
      disabled: boolean,
      options: any[],
      localError: boolean,
      placeHolder: string,
      value: any,
      setValue: any
    ) => {
      const helper =
        (options &&
          options.length &&
          value?.id &&
          options.find((option) => option.id === value?.id)) ||
        null;
      return (
        <AutoCompleteInput
          closeOnSelect
          disabled={!!disabled}
          onChange={(event: any, newValue: any | null) => {
            if (newValue?.createValue) {
              navigate('/app/catalog/terms', {
                state: { ...values, lastPage: '/app/leads/new', selectedTerms }
              });
              return;
            }
            setValue(newValue);
            setLoading(true);
          }}
          options={options}
          renderInput={(params) => (
            <DatePickerTextField
              error={localError}
              helperText={localError && 'Select terms and conditions'}
              placeholder={placeHolder}
              {...params}
            />
          )}
          sx={{
            margin: 0
          }}
          value={helper}
        />
      );
    };

    const handlePreviewContract = () => {
      setOpenPreview(true);
    };

    // Reset PDF generation state
    const resetPdfState = useCallback(() => {
      setPDFUrl('');
      setTemplate('');
      setLoading(true);
    }, []);

    // Map variables from props to template variables
    const mapVariables = useCallback(
      ({ values, proposalData }: any) => ({
        [ContractHeaderVariablesEnum.clientName]: values?.client?.businessName,
        [ContractHeaderVariablesEnum.clientAddress]:
          values?.destinations?.[0]?.address?.address,
        [ContractHeaderVariablesEnum.companyName]: values?.company?.companyName,
        [ContractHeaderVariablesEnum.startDate]: formatDate(
          values?.projectStartDate
        ),
        [ContractHeaderVariablesEnum.endDate]: formatDate(
          values?.projectEndDate
        ),
        [PaymentTermsVariablesEnum.invoiceFrequency]: proposalData?.frequency,
        [PaymentTermsVariablesEnum.securityDeposit]:
          proposalData?.depositAmount || 0,
        [PaymentTermsVariablesEnum.sendOn]: proposalData?.frequencyDay,
        [PaymentTermsVariablesEnum.paymentTerms]: proposalData?.paymentTerms
      }),
      []
    );

    const variables = useMemo(() => {
      if (values && proposalData) {
        return mapVariables({ proposalData, values });
      }
      return {};
    }, [values, proposalData, mapVariables]);

    // Function to generate PDF from HTML template
    const generatePDF = useCallback(async (stringTemplate: string) => {
      if (stringTemplate === '') {
        return;
      }

      const element = document.createElement('div');
      element.innerHTML = stringTemplate;
      const options = {
        filename: 'contract.pdf',
        html2canvas: { scale: 2 },
        image: { quality: 0.98, type: 'jpeg' },
        jsPDF: { format: 'letter', orientation: 'portrait', unit: 'in' },
        margin: [0.5, 0.5, 0.5, 0.5],
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      const pdf = await html2pdf().from(element).set(options).outputPdf('blob');
      // eslint-disable-next-line consistent-return
      return pdf;
    }, []);

    // Function to populate services section in the template
    const populateServices = useCallback(
      (template: string) => {
        if (!values?.destinations) {
          return template;
        }

        const serviceTimesList = values.destinations.reduce(
          (acc: any, destination: any) => {
            // eslint-disable-next-line no-unsafe-optional-chaining
            acc.push(...destination?.serviceTimes);
            return acc;
          },
          []
        );
        const serviceTimeContent = serviceTimesList.map(
          (serviceTime: any, i: number) => {
            const { frequencyDays, services } = serviceTime;
            let service = `<p><b>Service Time #${i + 1}:</b><br>
            Days: ${frequencyDays.join(', ')}<br>
            Services: ${services.map((s: any) => s.name)}<br>
          </p>`;
            if ((i + 1) % 4 === 0) {
              service = `${service}<div style="page-break-before: always;"></div>`;
            }
            return service;
          }
        );
        return `
      ${template}
        <div style="break-after:page"></div>
        <h2>Services</h2>
        ${serviceTimeContent.join('')}
      `;
      },
      [values]
    );

    // Function to fetch content files
    const fetchContentFiles = useCallback(async () => {
      if (
        !termsFiles ||
        !headerFiles ||
        termsFiles.length === 0 ||
        headerFiles.length === 0 ||
        !selectedTerms
      ) {
        return { headerContent: null, termsContent: null };
      }

      const headerContent = await readFileContent(headerFiles[0]?.fileUrl);
      const termsContent = await readFileContent(termsFiles[0]?.fileUrl);

      return { headerContent, termsContent };
    }, [termsFiles, headerFiles, selectedTerms]);

    // Function to create the template
    const createTemplate = useCallback(
      (headerContent: string, termsContent: string) => {
        return `
        ${headerContent}
        ${termsContent}
      `;
      },
      []
    );

    // Function to create a PDF file and URL
    const createPDFFile = useCallback(
      async (pdfBlob: Blob) => {
        const pdfFileName = `contract_${values?.id}.pdf`;
        const pdfFileWithName = new File([pdfBlob], pdfFileName, {
          type: 'application/pdf'
        });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        return { pdfFile: pdfFileWithName, pdfUrl };
      },
      [values]
    );

    // Main function to handle the PDF generation workflow
    const generateContractPDF = useCallback(async () => {
      // eslint-disable-next-line no-console
      try {
        const { headerContent, termsContent } = await fetchContentFiles();

        if (!headerContent || !termsContent) {
          setLoading(false);
          return;
        }

        // Create and parse template
        const template = createTemplate(headerContent, termsContent);
        let parsedTemplate = parseTemplate(template, variables);
        parsedTemplate = populateServices(parsedTemplate);
        setTemplate(parsedTemplate);

        // Generate PDF
        const pdfBlob = await generatePDF(parsedTemplate);
        const { pdfFile, pdfUrl } = await createPDFFile(pdfBlob);
        setPDFUrl(pdfUrl);
        setPdfFilePayload({ pdfFile, pdfUrl });
        setLoading(false);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error generating PDF:', error);
        setLoading(false);
      }
    }, [
      fetchContentFiles,
      createTemplate,
      variables,
      generatePDF,
      populateServices,
      createPDFFile,
      values,
      openPreview
    ]);

    // PDF Generation useEffects
    useEffect(() => {
      if (selectedTerms) {
        setGetTermsAPI(true);
        // Reset the PDF state when selectedTerms changes to ensure a fresh generation
        resetPdfState();
      }
    }, [selectedTerms, resetPdfState]);

    useEffect(() => {
      if (company?.id) {
        setGetHeaderAPI(true);
      }
    }, [company]);

    useEffect(() => {
      if (termsFileData) {
        setGetTermsAPI(false);
        setTermsFiles(termsFileData);
      }
    }, [termsFileData]);

    useEffect(() => {
      if (headerData) {
        setGetHeaderAPI(false);
        setHeaderFiles(headerData);
      }
    }, [headerData]);

    // Trigger PDF generation when content files are available
    useEffect(() => {
      if (
        termsFiles &&
        headerFiles &&
        termsFiles.length > 0 &&
        headerFiles.length > 0 &&
        selectedTerms
      ) {
        // eslint-disable-next-line no-console
        generateContractPDF();
      }
    }, [termsFiles, headerFiles, selectedTerms, generateContractPDF]);

    useEffect(() => {
      if (termsError || headerError) {
        setGetHeaderAPI(false);
        setGetTermsAPI(false);
        resetPdfState();
        setLoading(false);
      }
    }, [termsError, headerError, resetPdfState]);

    useEffect(() => {
      return () => {
        mutateTerms(undefined, true);
        mutateHeader(undefined, true);
        setLoading(false);
        setPDFUrl('');
        setTemplate('');
        setTermsFiles([]);
        setHeaderFiles([]);
        setFilePayload({});
        setGetHeaderAPI(false);
        setGetTermsAPI(false);
      };
    }, []);

    const documentViewer = <PdfViewerComponent file={PDFUrl} />;

    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="24px"
        padding="24px"
        sx={{
          border: `1px solid ${secondaryColors.SECONDARY_1}`,
          borderRadius: '8px'
        }}
      >
        <Box display="flex" flexDirection="column" gap="8px">
          <Typography
            color={secondaryColors.SECONDARY_5}
            variant="secondary-semi"
          >
            Select terms and conditions
          </Typography>
          <Typography color={secondaryColors.SECONDARY_4} variant="body_200">
            Select the terms and conditions that apply to this lead.
          </Typography>
          <Box mt={2}>
            {autoCompleteCustom(
              'termsAndConditions',
              false,
              allTermsAndConditions,
              !selectedTerms,
              'Select terms and conditions',
              selectedTerms,
              setSelectedTerms
            )}
          </Box>
          <Box mt={2}>
            <Button
              color="primary"
              onClick={() => {
                setSelectedTerms(null);
              }}
              sx={{ mr: 2 }}
              variant="outlined"
            >
              Reset
            </Button>
            <Button
              color="primary"
              disabled={!selectedTerms}
              onClick={handlePreviewContract}
              variant="contained"
            >
              Preview contract
            </Button>
          </Box>
        </Box>
        <Modal onClose={() => setOpenPreview(false)} open={openPreview}>
          <ModalContainer>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '2px',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 'bolder',
                  variant: 'title'
                }}
              >
                Preview
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <IconButton
                  aria-label="menu-close"
                  color="inherit"
                  edge="start"
                  onClick={() => setOpenPreview(false)}
                  sx={{ mr: 2 }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', width: '100%' }}>{documentViewer}</Box>
          </ModalContainer>
        </Modal>
      </Box>
    );
  };

export default SelectTermsAndConditions;
